import { Tab } from '@headlessui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import React, { useMemo, useState } from 'react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

type TabConfig = {
  name: string;
  content: React.ReactNode;
  href?: string;
};

export function LayoutPanels({
  tabs,
  rightExtra,
}: {
  tabs: TabConfig[];
  rightExtra?: React.ReactNode;
}) {
  const { asPath } = useRouter();
  const activeTab = useMemo(
    () => tabs.find((tab) => tab.href === asPath) || tabs[0],
    [asPath, tabs],
  );

  return (
    <>
      <div className="mb-4 flex justify-between items-end flex-wrap gap-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.length > 1 &&
            tabs.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                shallow
                replace
                className={classNames(
                  activeTab.href === tab.href
                    ? 'bg-mainDark/60 shadow text-main hover:text-main'
                    : 'text-gray-100 hover:text-main hover:bg-mainDark/30',
                  'px-6 py-2 font-medium rounded-lg no-underline',
                )}
                aria-current={activeTab.href === tab.href ? 'page' : undefined}>
                {tab.name}
              </Link>
            ))}
        </nav>

        {rightExtra}
      </div>

      {activeTab.content}
    </>
  );
}

export function Panels({ tabs }: { tabs: TabConfig[] }) {
  const getTabClassName = ({ selected }) =>
    classNames(
      'w-full py-2.5 text-sm leading-5 rounded-lg',
      'focus:outline-none',
      selected
        ? 'bg-mainDark shadow'
        : 'bg-dark1 hover:bg-white/[0.12] hover:text-white',
    );

  return (
    <Tab.Group>
      <Tab.List className="flex p-1 space-x-1 rounded-xl">
        {tabs.map((t) => (
          <Tab key={t.name} className={getTabClassName}>
            {t.name}
          </Tab>
        ))}
      </Tab.List>

      <Tab.Panels className="mt-2">
        {tabs.map((t) => (
          <Tab.Panel key={t.name}>{t.content}</Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
}
